/**
 * TanStack Query hooks for document validation
 */

import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { closingDisclosureApi } from '../services/closingDisclosureApi';
import type { ValidationSession } from '../types';

/**
 * Query keys for validation
 */
export const validationQueryKeys = {
  session: (sessionId: string) => ['validation', 'session', sessionId] as const,
};

/**
 * Fetch a validation session by ID
 */
export function useValidationSession(
  sessionId: string,
  options?: Omit<UseQueryOptions<ValidationSession, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: validationQueryKeys.session(sessionId),
    queryFn: async () => {
      const response = await closingDisclosureApi.getValidationSession(sessionId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to get validation session');
      }
      return response.data as unknown as ValidationSession;
    },
    enabled: !!sessionId,
    refetchInterval: (data) => {
      // Poll if status is 'pending' (waiting for extraction to complete)
      // Stop polling once we have extracted data
      if (!data) {
        return 2000; // Poll every 2 seconds
      }
      return false;
    },
    ...options,
  });
}

/**
 * Update validation session (auto-save)
 */
export function useUpdateValidationSession(
  options?: Omit<
    UseMutationOptions<
      { success: boolean; data: { sessionId: string; selectedFields: Record<string, unknown> } },
      Error,
      { sessionId: string; selectedFields: Record<string, unknown> }
    >,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sessionId, selectedFields }) => {
      return closingDisclosureApi.updateValidationSession(sessionId, selectedFields);
    },
    onSuccess: (_, variables) => {
      // Invalidate the session query to refetch
      queryClient.invalidateQueries({
        queryKey: validationQueryKeys.session(variables.sessionId),
      });
    },
    ...options,
  });
}

/**
 * Submit validated data
 */
export function useSubmitValidationSession(
  options?: Omit<
    UseMutationOptions<
      { success: boolean; message?: string; redirectUrl?: string },
      Error,
      { sessionId: string; selectedFields?: Record<string, unknown> }
    >,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sessionId, selectedFields }) => {
      const response = await closingDisclosureApi.submitValidationSession(
        sessionId,
        selectedFields,
      );
      if (!response.success) {
        throw new Error(response.error || 'Failed to submit validation session');
      }
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate the session query
      queryClient.invalidateQueries({
        queryKey: validationQueryKeys.session(variables.sessionId),
      });
      // Also invalidate documents list
      queryClient.invalidateQueries({
        queryKey: ['documents'],
      });
    },
    ...options,
  });
}

/**
 * Cancel validation session
 */
export function useCancelValidationSession(
  options?: Omit<
    UseMutationOptions<{ success: boolean; message: string }, Error, string>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await closingDisclosureApi.cancelValidationSession(sessionId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to cancel validation session');
      }
      return { success: true, message: response.message || 'Validation session cancelled' };
    },
    onSuccess: (_, sessionId) => {
      // Invalidate the session query
      queryClient.invalidateQueries({
        queryKey: validationQueryKeys.session(sessionId),
      });
    },
    ...options,
  });
}
